// SPDX-License-Identifier: AGPL-3.0-or-later

// 3D room body emotes: the shop's room_emote model keys, their room data-channel codes and the character clip each
// plays (`emote_<key>`). The wave is not here: everyone always has it.
export const ROOM_EMOTE_CODES = {dance: 'd', jump: 'j', crouch: 's', punch: 'u', faint: 'f'} as const;

export type RoomBodyEmoteKey = keyof typeof ROOM_EMOTE_CODES;

export const ROOM_BODY_EMOTES = Object.keys(ROOM_EMOTE_CODES) as ReadonlyArray<RoomBodyEmoteKey>;

// LiveKit participant attribute the API writes with the body emotes this person may play; clients only read it, and
// drop a body emote whose sender's list lacks it. Value: `1;<code>,<code>` in manifest order.
export const ROOM_EMOTES_ATTRIBUTE = 'micup.emotes';
export const ROOM_EMOTES_ATTRIBUTE_MAX_BYTES = 256;

const VERSION = '1;';
const KEY_BY_CODE = new Map<string, RoomBodyEmoteKey>(ROOM_BODY_EMOTES.map((key) => [ROOM_EMOTE_CODES[key], key]));

export function isRoomBodyEmoteKey(value: string): value is RoomBodyEmoteKey {
	return Object.hasOwn(ROOM_EMOTE_CODES, value);
}

export function encodeRoomEmotes(keys: Iterable<RoomBodyEmoteKey>): string {
	const owned = new Set(keys);
	return (
		VERSION +
		ROOM_BODY_EMOTES.filter((key) => owned.has(key))
			.map((key) => ROOM_EMOTE_CODES[key])
			.join(',')
	);
}

// The body emotes in an attribute value, or null when it is missing or not this format (then only the wave plays).
export function decodeRoomEmotes(value: string | undefined): Set<RoomBodyEmoteKey> | null {
	if (!value?.startsWith(VERSION) || value.length > ROOM_EMOTES_ATTRIBUTE_MAX_BYTES) return null;
	const body = value.slice(VERSION.length);
	const keys = new Set<RoomBodyEmoteKey>();
	if (body === '') return keys;
	for (const code of body.split(',')) {
		const key = KEY_BY_CODE.get(code);
		if (!key) return null;
		keys.add(key);
	}
	return keys;
}
